// SPDX-License-Identifier: AGPL-3.0-or-later

export type NotificationClearKind = 'message' | 'reaction';

export const REACTION_TAG_PREFIX = 'reaction:';

export interface NotificationClearInput {
	readonly kind: NotificationClearKind;
	readonly messageId: string | null | undefined;
	readonly ackMessageId: string | null | undefined;
}

export interface ReactionNotificationTag {
	readonly channelId: string | null;
	readonly messageId: string;
	readonly reactorId: string;
}

export function parseSnowflake(value: string | null | undefined): bigint | null {
	if (value == null || value.length === 0 || !/^\d+$/.test(value)) {
		return null;
	}
	try {
		return BigInt(value);
	} catch {
		return null;
	}
}

export function isAtOrBelowAckFloor(
	messageId: string | null | undefined,
	ackMessageId: string | null | undefined,
): boolean {
	const message = parseSnowflake(messageId);
	const floor = parseSnowflake(ackMessageId);
	if (message == null || floor == null) {
		return false;
	}
	return message <= floor;
}

export function shouldClosePushNotificationOnChannelClear({
	kind,
	messageId,
	ackMessageId,
}: NotificationClearInput): boolean {
	if (kind === 'reaction') {
		return false;
	}
	if (parseSnowflake(messageId) == null || parseSnowflake(ackMessageId) == null) {
		return true;
	}
	return isAtOrBelowAckFloor(messageId, ackMessageId);
}

export function shouldCloseTrackedNotificationOnAck({kind, messageId, ackMessageId}: NotificationClearInput): boolean {
	if (kind === 'reaction') {
		return false;
	}
	return isAtOrBelowAckFloor(messageId, ackMessageId);
}

export function resolveNotificationKindFromTag(tag: string | null | undefined): NotificationClearKind {
	return typeof tag === 'string' && tag.startsWith(REACTION_TAG_PREFIX) ? 'reaction' : 'message';
}

export function parseReactionNotificationTag(tag: string | null | undefined): ReactionNotificationTag | null {
	if (typeof tag !== 'string' || !tag.startsWith(REACTION_TAG_PREFIX)) {
		return null;
	}
	const segments = tag.split(':');
	if (segments.length === 4) {
		const [, channelId, messageId, reactorId] = segments;
		if (channelId.length === 0 || messageId.length === 0 || reactorId.length === 0) {
			return null;
		}
		return {channelId, messageId, reactorId};
	}
	if (segments.length === 3) {
		const [, messageId, reactorId] = segments;
		if (messageId.length === 0 || reactorId.length === 0) {
			return null;
		}
		return {channelId: null, messageId, reactorId};
	}
	return null;
}
